import { Moment } from 'moment';

/**
 * Pure countdown/format-helpers voor de transits-feature (spec §4a).
 * De keys verwijzen naar de i18n-keys onder `humanDesign.chart.transits.panel.*`
 * (worden in F2 toegevoegd); de params matchen de interpolatie-namen daarin.
 */

export type HdTransitCountdownKey = 'countdownMinutes' | 'countdownHours' | 'countdownDays' | 'countdownMonths';

export interface HdTransitCountdownFormat {
  readonly key: HdTransitCountdownKey;
  readonly params: Record<string, number>;
}

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
/** Gemiddelde maandlengte in dagen (365,25 / 12) voor de grove maand-countdown. */
const DAYS_PER_MONTH = 30.44;

const MINUTES_PER_HOUR_LIMIT = 60;
const HOURS_PER_DAY_LIMIT = 48;
const DAYS_LIMIT = 60;

/**
 * Kiest het countdown-formaat op basis van de resterende duur (spec §4a):
 * - `< 60 min`  → `countdownMinutes` (`{ minutes }`, min 1 — geen "nog 0m");
 * - `< 48 u`    → `countdownHours` (`{ hours, minutes }`);
 * - `< 60 d`    → `countdownDays` (`{ days, hours }`);
 * - anders      → `countdownMonths` (`{ months }`, 30,44 d/maand, naar beneden
 *   afgerond, min 1).
 *
 * Alle eenheden worden naar beneden afgerond: een countdown overdrijft zo nooit.
 * Negatieve resttijd (shift al gepasseerd) gedraagt zich als 0.
 */
export function selectCountdownFormat(remainingMs: number): HdTransitCountdownFormat {
  const remaining = Math.max(0, remainingMs);

  if (remaining < MINUTES_PER_HOUR_LIMIT * MS_PER_MINUTE) {
    return {
      key: 'countdownMinutes',
      params: { minutes: Math.max(1, Math.floor(remaining / MS_PER_MINUTE)) },
    };
  }
  if (remaining < HOURS_PER_DAY_LIMIT * MS_PER_HOUR) {
    return {
      key: 'countdownHours',
      params: {
        hours: Math.floor(remaining / MS_PER_HOUR),
        minutes: Math.floor((remaining % MS_PER_HOUR) / MS_PER_MINUTE),
      },
    };
  }
  if (remaining < DAYS_LIMIT * MS_PER_DAY) {
    return {
      key: 'countdownDays',
      params: {
        days: Math.floor(remaining / MS_PER_DAY),
        hours: Math.floor((remaining % MS_PER_DAY) / MS_PER_HOUR),
      },
    };
  }
  return {
    key: 'countdownMonths',
    params: { months: Math.max(1, Math.floor(remaining / (DAYS_PER_MONTH * MS_PER_DAY))) },
  };
}

/** `"31.1"` met lijn, `"31"` zonder lijn (gate-granulariteit). */
export function formatGateLine(gate: number, line: number | null | undefined): string {
  return line == null ? `${gate}` : `${gate}.${line}`;
}

/** Resterende tijd van `now` tot `target` in ms (negatief als `target` gepasseerd is). */
export function msUntil(target: Moment, now: Moment): number {
  return target.valueOf() - now.valueOf();
}
